import { Box, TablePagination } from "@mui/material";
import * as React from "react";

// TODO (ROBO-1292) Refactor FileGrid to be Dataset agnostic and move it to /shared/components
import { DatasetFileGrid } from "@/Datasets/Dataset/DatasetFileBrowser/DatasetFileGrid";
import { FileRecord } from "@/shared/domain/files";
import {
  FileNode,
  FileSystemNode,
  isFile,
} from "@/shared/domain/files/filesystem";

interface PaginationConfig {
  page: number;
  rowsPerPage: 10 | 25 | 50;
  pageTokens: { [key: number]: string | undefined };
}

export interface FileGridProps {
  files?: FileRecord[];
  selectedFiles?: FileNode[];
  setSelectedFiles?: (arg: FileNode[]) => void;
  fetchMoreData?: () => void;
}

export const FileGrid: React.FC<FileGridProps> = ({
  files,
  selectedFiles,
  setSelectedFiles = () => {},
  fetchMoreData,
}) => {
  const [currentPage, setCurrentPage] = React.useState<FileNode[] | null>(null);

  const [tableConfig, setTableConfig] = React.useState<PaginationConfig>({
    page: 0,
    rowsPerPage: 25,
    pageTokens: {},
  });

  const fileNodes = React.useMemo(() => {
    if (!files) {
      return null;
    }
    return files.map((file) => {
      return {
        type: "file",
        name: file.relative_path,
        file,
      } as FileNode;
    });
  }, [files]);

  const numFiles = files?.length ?? 0;

  const isLastPageOfFiles =
    (tableConfig.page + 1) * tableConfig.rowsPerPage >= numFiles;

  React.useEffect(() => {
    // Constrain rows to current page size
    if (fileNodes !== null) {
      setCurrentPage(
        fileNodes.slice(
          tableConfig.page * tableConfig.rowsPerPage,
          tableConfig.page * tableConfig.rowsPerPage + tableConfig.rowsPerPage,
        ),
      );
    }
  }, [fileNodes, tableConfig.page, tableConfig.rowsPerPage]);

  React.useEffect(() => {
    setTableConfig({
      rowsPerPage: tableConfig.rowsPerPage,
      page: 0,
      pageTokens: {},
    });
  }, [tableConfig.rowsPerPage]);

  return (
    <>
      <DatasetFileGrid
        currentPage={currentPage}
        gridItems={3}
        selectedItems={selectedFiles}
        datasetId=""
        isSearching={true}
        handleSelectRows={(rows: FileSystemNode[], isSelected: boolean) => {
          if (!rows.every((row) => isFile(row))) {
            return;
          }

          if (selectedFiles) {
            if (isSelected) {
              const itemsToAdd = [];
              for (const row of rows) {
                if (
                  !selectedFiles.some((item) => {
                    if (!isFile(item)) {
                      return false;
                    }

                    if (!isFile(row)) {
                      return false;
                    }

                    return item.file.file_id === row.file.file_id;
                  })
                ) {
                  itemsToAdd.push(row);
                }
              }
              const newState = selectedFiles.concat(itemsToAdd);
              setSelectedFiles(newState);
            } else {
              const newState = selectedFiles.filter(
                (item) =>
                  !rows.some((row) => {
                    if (!isFile(row)) {
                      return false;
                    }

                    return row.file.file_id === item.file.file_id;
                  }),
              );
              setSelectedFiles(newState);
            }
          } else {
            if (isSelected) {
              setSelectedFiles(rows);
            }
          }
        }}
      />
      <TablePagination
        component={Box}
        count={isLastPageOfFiles ? numFiles : -1}
        page={tableConfig.page}
        rowsPerPage={tableConfig.rowsPerPage}
        slotProps={{
          actions: {
            nextButton: {
              disabled: isLastPageOfFiles,
            },
          },
        }}
        labelRowsPerPage={"Items per page:"}
        rowsPerPageOptions={[10, 25, 50]}
        onPageChange={(_event, newPage) => {
          if (
            files &&
            (newPage + 2) * tableConfig.rowsPerPage >= files.length
          ) {
            // Request more pages of data from the backend
            // as we approach the last page of cached results.
            // 2 pages prior seemed to work well experimentally.
            fetchMoreData?.();
          }
          setTableConfig((prevConfig) => ({
            ...prevConfig,
            page: newPage,
          }));
        }}
        onRowsPerPageChange={(event) => {
          setTableConfig({
            rowsPerPage: parseInt(event.target.value, 10) as 10 | 25 | 50,
            page: 0,
            pageTokens: {},
          });
        }}
      />
    </>
  );
};
